import { AKComponent } from '@theme/component';
import { DialogCloseEvent } from '@theme/events';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 * @property {HTMLElement} placeholder - The placeholder div for autoplay mode
 * @property {HTMLElement} placeholderPoster - The placeholder with poster image
 * @property {HTMLIFrameElement} videoIframe - The video iframe element
 * @property {HTMLVideoElement} videoElement - The video element
 *
 * @extends {AKComponent<Refs>}
 */
class DeferredMedia extends AKComponent {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  isLoaded = false;

  /** @type {boolean} */
  isVisible = false;

  #abortController = new AbortController();
  #intersectionObserver = /** @type {IntersectionObserver | null} */ (null);

  constructor() {
    super();
    this.boundPauseMedia = this.pauseMedia.bind(this);
    this.boundHandleIntersection = this.handleIntersection.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    
    // Event listeners - removed global media pause to allow multiple videos
    // document.addEventListener(ThemeEvents.mediaStartedPlaying, this.boundPauseMedia, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.boundPauseMedia, { signal });
    
    // Initialize observers ONLY if auto-load is enabled
    const autoLoadEnabled = this.hasAttribute('data-auto-load');
    
    if (autoLoadEnabled) {
      this.initializeObservers();
      this.checkInitialVisibility();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupObservers();
    this.#abortController.abort();
  }

  /**
   * Check initial visibility when component is connected
   */
  checkInitialVisibility() {
    // Petit délai pour s'assurer que le DOM est stable
    requestAnimationFrame(() => {
      this.checkVisibility();
    });
  }

  /**
   * Initialize Intersection Observer for automatic loading
   */
  initializeObservers() {
    // Vérifier si le chargement automatique est activé
    const autoLoadEnabled = this.hasAttribute('data-auto-load');
    if (!autoLoadEnabled) return;

    // Intersection Observer pour détecter quand la vidéo devient visible
    if ('IntersectionObserver' in window) {
      this.#intersectionObserver = new IntersectionObserver(
        this.boundHandleIntersection,
        {
          root: null,
          rootMargin: '50px', // Commencer le chargement 50px avant que l'élément soit visible
          threshold: 0.1
        }
      );
      this.#intersectionObserver.observe(this);
    }
  }

  /**
   * Cleanup observers
   */
  cleanupObservers() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /**
   * Handle intersection changes
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.target === this) {
        this.isVisible = entry.isIntersecting;
        
        // Charger la vidéo si elle devient visible et n'est pas encore chargée
        if (this.isVisible && !this.isLoaded) {
          this.loadVideoWhenVisible();
        }
      }
    });
  }

  /**
   * Check if the element is currently visible in the viewport
   * @returns {boolean} Whether the element is visible
   */
  checkVisibility() {
    if (this.isLoaded) return false;
    
    const rect = this.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    // Vérifier si l'élément est dans le viewport avec une marge
    const isVisible = (
      rect.top < windowHeight + 50 &&
      rect.bottom > -50 &&
      rect.left < windowWidth + 50 &&
      rect.right > -50
    );
    
    if (isVisible) {
      this.loadVideoWhenVisible();
      return true;
    }
    
    return false;
  }

  /**
   * Load video when it becomes visible
   */
  loadVideoWhenVisible() {
    if (this.isLoaded) return;
    
    // Charger le contenu différé sans mettre le focus (chargement automatique)
    this.loadContent(false);
    this.isLoaded = true;
    
    // Vérifier si on doit jouer automatiquement
    const autoPlayEnabled = this.hasAttribute('data-auto-play');
    
    // Ne jouer automatiquement que si explicitement demandé
    if (autoPlayEnabled && this.isVisible) {
      // Petit délai pour s'assurer que le contenu est chargé
      setTimeout(() => {
        this.playMedia();
      }, 100);
    }
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      const isLoaded = this.getAttribute('data-media-loaded');
      
      
      
      if (isLoaded) {
        // Vidéo chargée : gérer l'état play/pause
        if (isPlaying) {
          // En cours de lecture : cacher le bouton toggle
          toggleMediaButton.classList.add('c-deferred-media__btn--hidden');
          toggleMediaButton.classList.add('c-deferred-media__playing');
          
        } else {
          // En pause : montrer le bouton toggle
          toggleMediaButton.classList.remove('c-deferred-media__btn--hidden');
          toggleMediaButton.classList.remove('c-deferred-media__playing');
          
        }
      } else {
        // Vidéo non chargée : cacher le bouton toggle
        toggleMediaButton.classList.add('c-deferred-media__btn--hidden');
        toggleMediaButton.classList.remove('c-deferred-media__playing');
        
      }
      
      const playIcon = toggleMediaButton.querySelector('.c-deferred-media__play--play');
      if (playIcon) playIcon.classList.toggle('c-deferred-media__play--hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.c-deferred-media__play--pause');
      if (pauseIcon) pauseIcon.classList.toggle('c-deferred-media__play--hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    // Si la vidéo n'est pas encore chargée, la charger d'abord
    if (!this.isLoaded) {
      this.loadContent(true);
      this.isLoaded = true;
    }
    
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    // Removed MediaStartedPlayingEvent dispatch to allow multiple videos
    // this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('c-deferred-media__playing');

    // Hide placeholders when content is loaded
    this.refs.placeholder?.style.setProperty('display', 'none');
    this.refs.placeholderPoster?.style.setProperty('display', 'none');
    
    // Hide product media placeholder to prevent page jump
    const productMediaPlaceholder = this.closest('.product-media')?.querySelector('.product-media__placeholder');
    if (productMediaPlaceholder instanceof HTMLElement) {
      productMediaPlaceholder.style.setProperty('display', 'none');
    }

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    const iframe = this.refs.videoIframe;
    if (iframe) {
      
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      
      this.refs.videoElement?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    const iframe = this.refs.videoIframe;

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.refs.videoElement?.pause();
    }
    this.isPlaying = false;

    // Always update the play/pause hint when pausing
    this.updatePlayPauseHint(this.isPlaying);
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  constructor() {
    super();
    this.boundSetupModelViewerUI = this.setupModelViewerUI.bind(this);
  }

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.boundSetupModelViewerUI,
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
